// Eingang (rechte Spalte, Handoff §2.4): Kopf mit Status-Segment und Aktionen, Kennzahlen,
// interne Notiz, Abschnitte. Inhalte/Labels exakt wie bisher; nur Darstellung neu.
import { useState, type ReactNode } from 'react';
import {
  Briefcase, Buildings, CalendarBlank, CaretDown, CaretUp, Check, CheckCircle, CircleNotch, DotsThreeCircle, EnvelopeSimple,
  HouseLine, IdentificationCard, NotePencil, Phone, SealCheck, Trash, TrayArrowDown, User, UserCheck, UserPlus, Users, UsersThree,
  Wallet, WarningCircle, type Icon,
} from '@phosphor-icons/react';
import { STATUS_LABEL, STATUS_REIHENFOLGE, type SelbstauskunftStatus } from '../../../../lib/selbstauskunft/status';
import { STATUS_FARBE, initialen, personenText } from '../../../../lib/selbstauskunft/darstellung';
import { BESCHAEFTIGUNGSART_LABEL, formatDatum, personenzahl, wert, type Eingang } from './typen';
import { Avatar } from './EingangsListe';
import { STATUS_ICON } from './Uebersicht';

const GLAS_KARTE = { borderRadius: 22, padding: '16px 18px' } as const;

/** App-Icon 28 px (Radius 9, weißes Icon) für Abschnitts-Köpfe. */
function AbschnittsIcon({ icon: I, farbe, inkFarbe = '#fff' }: { icon: Icon; farbe: string; inkFarbe?: string }) {
  return (
    <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 28, height: 28, borderRadius: 9, background: farbe, color: inkFarbe }}>
      <I size={15} weight="fill" />
    </span>
  );
}

function Abschnitt({ titel, icon, farbe, rechts, children }: { titel: string; icon: Icon; farbe: string; rechts?: ReactNode; children: ReactNode }) {
  return (
    <section aria-label={titel} className="immo-glas flex min-w-0 flex-col" style={{ ...GLAS_KARTE, gap: 12 }}>
      <div className="flex items-center justify-between" style={{ gap: 10 }}>
        <h3 className="m-0 flex items-center" style={{ gap: 8, fontSize: 15, fontWeight: 800 }}>
          <AbschnittsIcon icon={icon} farbe={farbe} />{titel}
        </h3>
        {rechts}
      </div>
      {children}
    </section>
  );
}

function Feld({ label, value, breit, farbe }: { label: string; value: ReactNode; breit?: boolean; farbe?: string }) {
  return (
    <div className="flex min-w-0 flex-col" style={{ gap: 2, gridColumn: breit ? '1 / -1' : undefined }}>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink3)' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 700, overflowWrap: 'anywhere', color: farbe ?? 'var(--ink)' }}>{value}</span>
    </div>
  );
}

const Raster = ({ children }: { children: ReactNode }) => (
  <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: '12px 18px' }}>{children}</div>
);

/** Erklärungszeile — ein „Ja“ ist das Warnsignal, alles andere bleibt ruhig. */
function Erklaerung({ text, istJa }: { text: string; istJa?: boolean }) {
  const farbe = istJa ? 'var(--bad)' : 'var(--ok)';
  return (
    <div className="flex items-center" style={{ gap: 10, padding: '10px 12px', borderRadius: 14, background: istJa ? 'var(--bad-soft)' : 'var(--fill)' }}>
      {istJa ? <WarningCircle size={18} weight="fill" color={farbe} className="shrink-0" /> : <CheckCircle size={18} weight="fill" color={farbe} className="shrink-0" />}
      <span className="flex-1" style={{ fontSize: 13, fontWeight: 600, color: istJa ? 'var(--bad)' : 'var(--ink)' }}>{text}</span>
      <span style={{ fontSize: 12.5, fontWeight: 800, color: farbe }}>{istJa ? 'Ja' : 'Nein'}</span>
    </div>
  );
}

/**
 * Kennzahl-Kachel. Objekt/Einheit steht kleiner und darf umbrechen: „Hindenburgstr. 12a“ wurde in der
 * großen Zahlenschrift abgeschnitten und war kaum lesbar (Björn zu #375, 19.09.2026).
 */
function Kennzahl({ label, value, icon: I, farbe, groesse, unter }: { label: string; value: ReactNode; icon: Icon; farbe: string; groesse: 16 | 20 | 22; unter?: string | null }) {
  return (
    <div className="immo-glas relative flex min-w-0 flex-col overflow-hidden" style={{ borderRadius: 22, padding: '14px 16px', gap: 8, minHeight: 104 }}>
      <I aria-hidden size={70} weight="fill" className="pointer-events-none absolute" style={{ right: -10, bottom: -14, color: farbe, opacity: 0.12 }} />
      <span className="relative flex items-center" style={{ gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--ink2)' }}>
        <I size={14} weight="fill" color={farbe} className="shrink-0" />{label}
      </span>
      <span className="relative tabular-nums" style={{ fontSize: groesse, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, overflowWrap: 'anywhere' }}>{value}</span>
      {unter && <span className="relative" style={{ fontSize: 12, color: 'var(--ink2)', marginTop: -4 }}>{unter}</span>}
    </div>
  );
}

/**
 * Interne Notiz — gespeichert beim Verlassen des Felds. Steht bewusst außerhalb der Seite:
 * als innere Komponente würde sie bei jedem Render neu montiert und verlöre den Fokus.
 * Aufrufer setzt key = Eingangs-Id, damit ein Wechsel den Entwurf verwirft.
 */
export function InterneNotiz({ wert: anfang, onSpeichern }: { wert: string | null; onSpeichern: (text: string) => Promise<boolean> }) {
  const [text, setText] = useState(anfang || '');
  const [zustand, setZustand] = useState<'ruhe' | 'speichert' | 'gespeichert'>('ruhe');
  const speichern = async () => {
    if (text === (anfang || '')) return;
    setZustand('speichert');
    const ok = await onSpeichern(text);
    setZustand(ok ? 'gespeichert' : 'ruhe');
  };
  return (
    <section aria-label="Interne Notiz" className="immo-glas flex flex-col" style={{ ...GLAS_KARTE, gap: 10 }}>
      <div className="flex items-center justify-between" style={{ gap: 10 }}>
        <h3 className="m-0 flex items-center" style={{ gap: 8, fontSize: 15, fontWeight: 800 }}>
          <AbschnittsIcon icon={NotePencil} farbe="#ffcc00" inkFarbe="#3a2a00" />Interne Notiz
        </h3>
        <span role="status" className="flex items-center" style={{ height: 18, gap: 5, fontSize: 11.5, fontWeight: 700, color: zustand === 'gespeichert' ? 'var(--ok)' : 'var(--ink3)' }}>
          {zustand === 'speichert' && <><CircleNotch size={12} weight="bold" className="animate-spin" /> Speichert…</>}
          {zustand === 'gespeichert' && <><Check size={12} weight="bold" /> Gespeichert</>}
        </span>
      </div>
      <textarea value={text} rows={3} aria-label="Interne Notiz"
        onChange={(ev) => { setText(ev.target.value); setZustand('ruhe'); }}
        onBlur={() => { void speichern(); }}
        placeholder="Eindruck bei der Besichtigung, Absprachen, Rückfragen … (nur für uns sichtbar)"
        className="w-full"
        style={{ resize: 'vertical', border: 'none', outline: 'none', borderRadius: 14, padding: '12px 14px', background: 'var(--fill)',
          boxShadow: 'inset 0 0 0 1px var(--line)', fontFamily: 'inherit', fontSize: 14, lineHeight: 1.5, color: 'var(--ink)' }} />
    </section>
  );
}


export interface EingangAnsichtProps {
  e: Eingang;
  position: number;
  anzahl: number;
  onVorher: () => void;
  onNaechster: () => void;
  onStatus: (s: SelbstauskunftStatus) => void;
  onKontaktAnlegen: () => void;
  onKontaktOeffnen: () => void;
  onLoeschen: () => void;
  wirdGeloescht: boolean;
  onNotiz: (text: string) => Promise<boolean>;
  /** Handy: Tippflächen 44 px */
  mobil?: boolean;
}

export function EingangAnsicht({ e, position, anzahl, onVorher, onNaechster, onStatus, onKontaktAnlegen, onKontaktOeffnen, onLoeschen, wirdGeloescht, onNotiz, mobil = false }: EingangAnsichtProps) {
  const tipp = mobil ? 44 : null;
  const d = e.daten || {};
  const farbe = STATUS_FARBE[e.status];
  const pfeil = (gesperrt: boolean) => ({
    width: tipp ?? 30, height: tipp ?? 30, borderRadius: mobil ? 14 : 10, border: 'none', cursor: gesperrt ? 'default' : 'pointer', background: 'var(--fill2)', color: 'var(--ink)', opacity: gesperrt ? 0.3 : 1,
  });
  const beideEinwilligungen = d.einwilligung_datenschutz && d.wahrheitserklaerung;
  const CHIP = { height: tipp ?? 28, padding: '0 10px', borderRadius: mobil ? 22 : 14, gap: 5, textDecoration: 'none' } as const;

  return (
    <div className="flex flex-col immo-side-in" style={{ gap: 12 }}>
      {/* Kopf steht IMMER zweizeilig: Name oben, Knopfreihe darunter (Björns Screenshot zu #375, 18.09.2026). */}
      <section aria-label="Eingang" className="immo-glas flex flex-col" style={{ borderRadius: 26, padding: 20, gap: 16 }}>
        <div className="flex items-start" style={{ gap: 14 }}>
          <Avatar name={e.name} farbe={farbe} groesse={60} />
          <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 6 }}>
            <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
              <h2 className="m-0 max-w-full truncate" style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05 }}>{e.name}</h2>
              <span className="flex items-center" style={{ gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink3)', marginRight: 4 }}>{position} von {anzahl}</span>
                <button type="button" onClick={onVorher} disabled={position <= 1} aria-label="Vorheriger Eingang" data-tip="Vorheriger Eingang"
                  className="inline-flex items-center justify-center" style={pfeil(position <= 1)}><CaretUp size={13} weight="bold" /></button>
                <button type="button" onClick={onNaechster} disabled={position >= anzahl} aria-label="Nächster Eingang" data-tip="Nächster Eingang"
                  className="inline-flex items-center justify-center" style={pfeil(position >= anzahl)}><CaretDown size={13} weight="bold" /></button>
              </span>
            </div>
            <div className="flex flex-wrap" style={{ gap: 6, fontSize: 12.5 }}>
              <a href={`mailto:${e.email}`} data-tip={`E-Mail schreiben – ${e.email}`} className="inline-flex max-w-full items-center"
                style={{ ...CHIP, fontWeight: 700, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
                <EnvelopeSimple size={13} weight="fill" className="shrink-0" /><span className="truncate">{e.email}</span>
              </a>
              {e.telefon && (
                <a href={`tel:${e.telefon}`} data-tip={`Anrufen – ${e.telefon}`} className="inline-flex items-center"
                  style={{ ...CHIP, fontWeight: 700, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
                  <Phone size={13} weight="fill" />{e.telefon}
                </a>
              )}
              <span className="inline-flex items-center" style={{ ...CHIP, fontWeight: 600, background: 'var(--fill2)', color: 'var(--ink2)' }}>
                <TrayArrowDown size={13} />eingegangen {formatDatum(e.created_at)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
          <div role="group" aria-label="Status" className="flex flex-wrap" style={{ padding: 4, gap: 2, borderRadius: 20, background: 'var(--fill2)' }}>
            {STATUS_REIHENFOLGE.map((s) => {
              const an = e.status === s;
              const sf = STATUS_FARBE[s];
              const I = STATUS_ICON[s];
              return (
                <button key={s} type="button" disabled={an} aria-pressed={an} onClick={() => onStatus(s)}
                  data-tip={an ? `Aktueller Status: ${STATUS_LABEL[s]}` : `Status setzen – ${STATUS_LABEL[s]}`}
                  className="inline-flex items-center"
                  style={{ height: tipp ?? 34, padding: '0 12px', gap: 6, border: 'none', borderRadius: mobil ? 20 : 16, cursor: an ? 'default' : 'pointer', fontFamily: 'inherit', fontSize: 12.5,
                    fontWeight: an ? 800 : 600, background: an ? sf : 'transparent', color: an ? '#fff' : 'var(--ink2)', boxShadow: an ? `0 6px 14px -6px ${sf}` : 'none', transition: 'all .2s' }}>
                  <I size={14} weight="fill" color={an ? '#fff' : sf} />{STATUS_LABEL[s]}
                </button>
              );
            })}
          </div>
          {e.kontakt_id ? (
            <button type="button" onClick={onKontaktOeffnen} data-tip="Kontakt öffnen – Angelegten Kontakt öffnen" className="inline-flex items-center"
              style={{ height: tipp ?? 42, padding: '0 14px', gap: 7, borderRadius: 22, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 800, background: 'var(--ok-soft)', color: 'var(--ok)' }}>
              <UserCheck size={16} weight="fill" /> Kontakt öffnen
            </button>
          ) : (
            <button type="button" onClick={onKontaktAnlegen} data-tip="Kontakt anlegen – Kontakt mit den Angaben aus der Selbstauskunft anlegen" className="inline-flex items-center"
              style={{ height: tipp ?? 42, padding: '0 14px', gap: 7, borderRadius: 22, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 800,
                background: 'var(--acc-fill)', boxShadow: 'var(--acc-sh)', color: 'var(--acc-ink)' }}>
              <UserPlus size={16} weight="fill" /> Kontakt anlegen
            </button>
          )}
          <button type="button" onClick={onLoeschen} disabled={wirdGeloescht} aria-label="Selbstauskunft löschen" data-tip="Löschen – Selbstauskunft unwiderruflich löschen"
            className="ml-auto inline-flex items-center justify-center"
            style={{ width: tipp ?? 42, height: tipp ?? 42, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--bad-soft)', color: 'var(--bad)', opacity: wirdGeloescht ? 0.6 : 1 }}>
            {wirdGeloescht ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <Trash size={16} weight="bold" />}
          </button>
        </div>
      </section>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        {/* Netto ist Freitext: lange Angaben („3.400 € - beide Einziehende …“) kleiner, damit die Kachel lesbar bleibt */}
        <Kennzahl label="Haushalts-Netto" value={wert(d.netto_einkommen)} icon={Wallet} farbe="#24a35a" groesse={(d.netto_einkommen || '').length > 14 ? 16 : 22} />
        <Kennzahl label="Haushalt" value={personenText(personenzahl(e))} icon={UsersThree} farbe="#2f7cf6" groesse={22} />
        <Kennzahl label="Einzug gewünscht" value={formatDatum(d.einzugstermin)} icon={CalendarBlank} farbe="#ff9500" groesse={20} />
        <Kennzahl label="Objekt · Einheit" value={e.objekt?.bezeichnung || 'Kein Objekt'} unter={e.einheit?.bezeichnung} icon={Buildings} farbe="#8a5cf5" groesse={16} />
      </div>

      <InterneNotiz key={e.id} wert={e.notiz_intern} onSpeichern={onNotiz} />

      <div className="grid items-start" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(310px, 100%), 1fr))', gap: 12 }}>
        <Abschnitt titel="Persönliche Angaben" icon={User} farbe="#0a84ff">
          <Raster>
            <Feld label="Anrede" value={wert(d.anrede)} />
            <Feld label="Geburtsdatum" value={formatDatum(d.geburtsdatum)} />
            <Feld label="Familienstand" value={wert(d.familienstand)} />
            <Feld label="Telefon" value={wert(d.telefon)} />
            <Feld breit label="Anschrift" value={`${wert(d.strasse)}, ${wert(d.plz)} ${wert(d.ort)}`} />
            <Feld breit label="E-Mail" value={wert(d.email)} />
          </Raster>
        </Abschnitt>

        <Abschnitt titel="Erklärungen" icon={SealCheck} farbe="#ff3b30">
          <div className="flex flex-col" style={{ gap: 6 }}>
            <Erklaerung text="Mietschulden bei vorherigem Vermieter" istJa={d.erklaerungen?.mietschulden} />
            <Erklaerung text="Räumungsklage in den letzten 5 Jahren" istJa={d.erklaerungen?.raeumungsklage} />
            <Erklaerung text="Eidesstattl. Versicherung / Insolvenz" istJa={d.erklaerungen?.insolvenz} />
            <Erklaerung text="Lohn- / Gehaltspfändung" istJa={d.erklaerungen?.lohnpfaendung} />
          </div>
        </Abschnitt>

        <Abschnitt titel="Beschäftigung & Einkommen" icon={Briefcase} farbe="#34c759">
          <Raster>
            <Feld label="Beruf" value={wert(d.beruf)} />
            <Feld label="Beschäftigungsart" value={d.beschaeftigungsart ? (BESCHAEFTIGUNGSART_LABEL[d.beschaeftigungsart] || d.beschaeftigungsart) : '—'} />
            <Feld label="Arbeitgeber" value={wert(d.arbeitgeber)} />
            <Feld label="Ort" value={wert(d.arbeitgeber_ort)} />
            <Feld label="Beschäftigt seit" value={formatDatum(d.beschaeftigt_seit)} />
            <Feld label="Weitere Einkünfte" value={wert(d.weitere_einkuenfte)} />
          </Raster>
        </Abschnitt>

        <Abschnitt titel="Aktuelles Mietverhältnis" icon={HouseLine} farbe="#ff9500">
          <Raster>
            <Feld label="Wohnhaft seit" value={formatDatum(d.mietverhaeltnis?.wohnhaft_seit)} />
            <Feld label="Gekündigt" value={d.mietverhaeltnis?.gekuendigt ? 'Ja' : 'Nein'} />
            <Feld label="Mietrückstände" value={d.mietverhaeltnis?.mietrueckstaende ? 'Ja' : 'Nein'} farbe={d.mietverhaeltnis?.mietrueckstaende ? 'var(--bad)' : undefined} />
            <Feld label="Vermieter" value={wert(d.mietverhaeltnis?.vermieter_name)} />
            <Feld breit label="Vermieter-Kontakt" value={wert(d.mietverhaeltnis?.vermieter_kontakt)} />
          </Raster>
        </Abschnitt>

        <Abschnitt titel="Weitere einziehende Personen" icon={Users} farbe="#5856d6">
          {(d.personen || []).length === 0 ? (
            <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine weiteren Personen angegeben.</span>
          ) : (
            <div className="flex flex-col" style={{ gap: 6 }}>
              {(d.personen || []).map((p, i) => (
                <div key={i} className="flex items-center" style={{ gap: 10, padding: '8px 10px', borderRadius: 14, background: 'var(--fill)' }}>
                  <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 34, height: 34, borderRadius: '50%', fontSize: 12, fontWeight: 800, background: 'var(--fill3, var(--fill2))' }}>
                    {initialen(p.name || '')}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate" style={{ fontSize: 13.5, fontWeight: 700 }}>{wert(p.name)}</span>
                    <span style={{ fontSize: 11.5, color: 'var(--ink2)' }}>{wert(p.verhaeltnis)}</span>
                  </span>
                  <span className="tabular-nums" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink2)' }}>{formatDatum(p.geburtsdatum)}</span>
                </div>
              ))}
            </div>
          )}
        </Abschnitt>

        <Abschnitt titel="Sonstiges & Einwilligungen" icon={DotsThreeCircle} farbe="#8e8e93">
          <Raster>
            <Feld label="Haustiere" value={wert(d.haustiere)} />
            <Feld label="Raucher" value={d.raucher ? 'Ja' : 'Nein'} />
            <Feld label="Musikinstrumente" value={wert(d.musikinstrumente)} />
            <Feld label="Einwilligungen" farbe={beideEinwilligungen ? 'var(--ok)' : 'var(--bad)'}
              value={beideEinwilligungen
                ? 'Beide erteilt'
                : `${d.einwilligung_datenschutz ? 'Datenschutz' : 'Datenschutz fehlt'} · ${d.wahrheitserklaerung ? 'Wahrheit' : 'Wahrheit fehlt'}`} />
            <Feld breit label="Nachricht" value={wert(d.nachricht)} />
          </Raster>
        </Abschnitt>
      </div>
    </div>
  );
}

/** Kein Eingang gewählt (Text exakt wie bisher). */
export function KeinEingang() {
  return (
    <div className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 26, padding: '90px 30px', gap: 10 }}>
      <span className="inline-flex items-center justify-center" style={{ width: 60, height: 60, borderRadius: 20, background: 'var(--fill2)' }}>
        <IdentificationCard size={30} weight="fill" color="var(--ink3)" />
      </span>
      <span style={{ fontSize: 17, fontWeight: 800 }}>Kein Eingang gewählt</span>
      <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)', maxWidth: 300 }}>
        Wähle links eine Selbstauskunft — sie erscheint hier, ohne dass die Liste verschwindet.
      </span>
    </div>
  );
}
